import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '../utils/toast';
import { Plus, Edit, Trash2, Star, X } from 'lucide-react';
import axios from 'axios';
import UNIVERSAL_LANGUAGES from '@/utils/languages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeedbackManagementPageNew = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [feedbackQuestions, setFeedbackQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  
  // Form states
  const [questionLanguage, setQuestionLanguage] = useState('English');
  const [languageSearch, setLanguageSearch] = useState('');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [feedbackType, setFeedbackType] = useState(''); // 'rating' or 'subjective'
  const [questionText, setQuestionText] = useState('');
  const [isRequired, setIsRequired] = useState(true); // True = mandatory, False = optional
  const [ratingOptions, setRatingOptions] = useState([
    { label: 'Needs Improvement', value: 1 },
    { label: 'Average', value: 2 },
    { label: 'Good', value: 3 },
    { label: 'Very Good', value: 4 },
    { label: 'Excellent', value: 5 }
  ]);
  const [topicId, setTopicId] = useState('');
  const [trainingTopics, setTrainingTopics] = useState([]);
  
  const availableLanguages = UNIVERSAL_LANGUAGES;

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchData();
    fetchTrainingTopics();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/feedback/questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedbackQuestions(res.data);
    } catch (error) {
      toast.error('Failed to fetch feedback questions');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainingTopics = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/training/content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrainingTopics(res.data);
    } catch (error) {
      console.error('Failed to fetch training chapters:', error);
    }
  };

  const resetForm = () => {
    setQuestionLanguage('English');
    setFeedbackType('');
    setQuestionText('');
    setIsRequired(true);
    setRatingOptions([
      { label: 'Needs Improvement', value: 1 },
      { label: 'Average', value: 2 },
      { label: 'Good', value: 3 },
      { label: 'Very Good', value: 4 },
      { label: 'Excellent', value: 5 }
    ]);
    setTopicId('none');
    setEditingQuestion(null);
  };

  const handleOpenQuestionModal = () => {
    resetForm();
    setShowQuestionModal(true);
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setQuestionLanguage(question.language || 'English');
    setFeedbackType(question.question_type);
    setQuestionText(question.question_text);
    setIsRequired(question.is_required !== undefined ? question.is_required : true);
    if (question.question_type === 'rating' && question.options) {
      setRatingOptions(question.options);
    }
    setTopicId(question.topic_id || 'none');
    setShowQuestionModal(true);
  };

  const handleSaveQuestion = async () => {
    if (!questionLanguage || !feedbackType || !questionText) {
      toast.error('Please fill all required fields');
      return;
    }

    if (feedbackType === 'rating' && ratingOptions.length === 0) {
      toast.error('Please add rating options');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const payload = {
        language: questionLanguage,
        question_type: feedbackType,
        question_text: questionText,
        is_required: isRequired,
        options: feedbackType === 'rating' ? ratingOptions : [],
        topic_id: (topicId && topicId !== 'none') ? topicId : null
      };

      if (editingQuestion) {
        // Update existing question
        await axios.put(`${API}/feedback/questions/${editingQuestion.question_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Feedback question updated successfully');
      } else {
        // Create new question
        await axios.post(`${API}/feedback/questions`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Feedback question created successfully');
      }

      setShowQuestionModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save feedback question');
    }
  };

  const handleDeleteQuestion = async (questionId, createdById) => {
    // Only Super Admin can delete feedback questions
    if (user?.role !== 'Super Admin') {
      toast.error('Only Super Admin can delete feedback questions');
      return;
    }

    // Show confirmation dialog immediately
    const confirmMessage = 'Deleting this feedback question will also delete all responses submitted for it. Do you really want to continue?';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Proceed with cascade delete
      const response = await axios.delete(`${API}/feedback/questions/${questionId}?cascade_delete=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const deletedResponses = response.data.deleted_responses || 0;
      
      toast.success(
        deletedResponses > 0 
          ? `Feedback question and ${deletedResponses} response${deletedResponses !== 1 ? 's' : ''} deleted successfully`
          : 'Feedback question deleted successfully'
      );
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete feedback question');
    }
  };

  const handleOptionChange = (index, field, value) => {
    const updated = [...ratingOptions];
    updated[index][field] = value;
    setRatingOptions(updated);
  };

  const handleAddOption = () => {
    if (ratingOptions.length >= 5) {
      toast.error('Maximum 5 rating options allowed');
      return;
    }
    setRatingOptions([...ratingOptions, { label: '', value: ratingOptions.length + 1 }]);
  };

  const handleRemoveOption = (index) => {
    if (ratingOptions.length <= 1) {
      toast.error('At least 1 rating option is required');
      return;
    }
    setRatingOptions(ratingOptions.filter((_, i) => i !== index));
  };

  const filteredLanguages = availableLanguages.filter(lang =>
    lang.toLowerCase().includes(languageSearch.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">FEEDBACK MANAGEMENT</h1>
          <Button
            onClick={handleOpenQuestionModal}
            className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Feedback Question
          </Button>
        </div>

        {/* Feedback Questions List */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 text-[#0056B3]">Feedback Questions</h2>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading feedback questions...</p>
            </div>
          ) : feedbackQuestions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg mb-2">No feedback questions yet</p>
              <p className="text-sm">Click "Add Feedback Question" to create your first question</p>
            </div>
          ) : (
            <div className="space-y-3">
              {feedbackQuestions.map((question) => {
                // Only Super Admin can delete feedback questions
                const canDelete = user?.role === 'Super Admin';
                
                return (
                  <Card key={question.question_id} className="p-4 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {question.language}
                          </span>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {question.question_type === 'rating' ? (
                              <>
                                <Star className="w-3 h-3 mr-1" />
                                1-5 Rating
                              </>
                            ) : (
                              'Subjective'
                            )}
                          </span>
                          {question.topic_id && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Tagged to Topic
                            </span>
                          )}
                        </div>
                        <p className="text-gray-900 font-medium mb-1">{question.question_text}</p>
                        {question.question_type === 'rating' && question.options && (
                          <div className="flex gap-1 text-xs text-gray-600 mt-2">
                            {question.options.map((opt, idx) => (
                              <span key={idx} className="px-2 py-1 bg-gray-100 rounded">
                                {opt.value}★: {opt.label}
                              </span>
                            ))}
                          </div>
                        )}
                        <p className="text-sm text-gray-500 mt-2">
                          Created by: {question.created_by_name || 'Unknown'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditQuestion(question)}
                          data-tooltip="Edit Question"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteQuestion(question.question_id, question.created_by_id)}
                            data-tooltip="Delete Question"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Add/Edit Question Modal */}
        <Dialog open={showQuestionModal} onOpenChange={setShowQuestionModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden" hideClose={true}>
            <div className="flex items-center justify-between pb-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-semibold">
                {editingQuestion ? 'Edit Feedback Question' : 'Add Feedback Question'}
              </h3>
              <button
                onClick={() => {
                  setShowQuestionModal(false);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0 mt-4">
            <div className="space-y-4">
              {/* Language Selection */}
              <div>
                <Label>Select Language *</Label>
                <div className="relative">
                  <Input
                    value={showLanguageDropdown ? languageSearch : questionLanguage}
                    onChange={(e) => setLanguageSearch(e.target.value)}
                    onFocus={() => setShowLanguageDropdown(true)}
                    placeholder="Search language..."
                    className="mt-1"
                  />
                  {showLanguageDropdown && (
                    <>
                      <div
                        className="fixed inset-0 z-30"
                        onClick={() => setShowLanguageDropdown(false)}
                      />
                      <div className="absolute z-40 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredLanguages.map((lang) => (
                          <div
                            key={lang}
                            onClick={() => {
                              setQuestionLanguage(lang);
                              setLanguageSearch('');
                              setShowLanguageDropdown(false);
                            }}
                            className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${
                              questionLanguage === lang ? 'bg-blue-100' : ''
                            }`}
                          >
                            {lang}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Feedback Type */}
              <div>
                <Label>Feedback Type *</Label>
                <Select value={feedbackType} onValueChange={setFeedbackType}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choose feedback type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rating">⭐ 1-5 Star Rating</SelectItem>
                    <SelectItem value="subjective">📝 Subjective (Open Text)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Question Text */}
              <div>
                <Label>Feedback Question Text *</Label>
                <Textarea
                  value={questionText}
                  onChange={(e) => setQuestionText(e.target.value)}
                  placeholder="Enter your feedback question..."
                  className="mt-1 min-h-[100px]"
                />
              </div>

              {/* Required/Optional Toggle */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <input
                  type="checkbox"
                  id="isOptional"
                  checked={!isRequired}
                  onChange={(e) => setIsRequired(!e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="isOptional" className="flex-1 cursor-pointer">
                  <span className="text-sm font-medium text-gray-900">Mark as Optional Question</span>
                  <p className="text-xs text-gray-600 mt-1">
                    If checked, staff can skip this question. Otherwise, it's mandatory.
                  </p>
                </label>
                {!isRequired && (
                  <span className="px-2 py-1 text-xs font-semibold text-orange-700 bg-orange-100 rounded">
                    OPTIONAL
                  </span>
                )}
              </div>

              {/* Rating Options (only for rating type) */}
              {feedbackType === 'rating' && (
                <div>
                  <Label>Feedback Options (5 options for 1-5 stars) *</Label>
                  <div className="space-y-2 mt-2">
                    {ratingOptions.map((option, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="flex items-center justify-center w-12 px-2 py-1 bg-gray-100 rounded text-sm font-medium">
                          {option.value}★
                        </span>
                        <Input
                          value={option.label}
                          onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                          placeholder={`Label for ${option.value} star${option.value > 1 ? 's' : ''}...`}
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveOption(index)}
                          disabled={ratingOptions.length <= 1}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    ))}
                    {ratingOptions.length < 5 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddOption}
                        className="w-full"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Option
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Tag to Training Chapter (Optional) */}
              <div>
                <Label>Tag to Training Chapter (Optional)</Label>
                <Select value={topicId || "none"} onValueChange={(val) => setTopicId(val === "none" ? "" : val)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a topic (optional)" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="max-h-[200px] overflow-y-auto">
                    <SelectItem value="none">None (Not Tagged)</SelectItem>
                    {trainingTopics.map((topic) => (
                      <SelectItem key={topic.id} value={topic.id}>
                        {topic.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowQuestionModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveQuestion}
                  className="bg-[#0056B3] hover:bg-[#0056B3]/90"
                  disabled={!questionLanguage || !feedbackType || !questionText}
                >
                  {editingQuestion ? 'Update' : 'Create'} Question
                </Button>
              </div>
            </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default FeedbackManagementPageNew;
